import React from "react"
import {
  configureSearchStore,
  SearchState,
  SearchStore,
  SearchStoreOptions,
} from "./store"
import {
  Bucket,
  FacetConfig,
  facetsActions,
  facetsSelectors,
} from "./store/facets-slice"
import {
  FilterConfig,
  filtersActions,
  filtersSelectors,
} from "./store/filters-slice"
import { bindActionCreators, createSelector, Selector } from "@reduxjs/toolkit"
import {
  paginationActions,
  paginationSelectors,
} from "./store/pagination-slice"
import { resultsSelectors } from "./store/results-slice"
import { allFiltersCleared } from "./store/search-actions"
import {
  search,
  SearchQuery,
  SearchThunkOptions,
  selectSearchQuery,
} from "./store/search-thunk"
import createSearchClient, { SearchClientOptions } from "./search-client"
import { SearchResponse } from "./store/search-response"

const DEFAULT_SEARCH_THROTTLE_MS = 200

const searchActions = {
  ...paginationActions,
  ...filtersActions,
  ...facetsActions,
  search,
  allFiltersCleared,
}

const searchSelectors = {
  ...facetsSelectors,
  ...filtersSelectors,
  ...resultsSelectors,
  ...paginationSelectors,
}

const {
  selectFacetBuckets,
  selectFacetRegistered,
  selectFilterValues,
  selectFilterRegistered,
  selectHits,
  selectStatus,
  selectResultsPagination,
  selectFacetIds,
  selectFilterIds,
  selectQueryFilters,
} = searchSelectors

type UseSearchProps = Omit<SearchThunkOptions, "options"> &
  Partial<Omit<SearchStoreOptions, "searchClient">> &
  SearchClientOptions
export type UseSearchReturn = {
  facets: {
    register(id: string, config: FacetConfig): void
    deregister(id: string): void
    registeredSelector(id: string): Selector<SearchState, boolean>
    bucketsSelector(id: string): Selector<SearchState, Bucket[] | null>
    selectIds: Selector<SearchState, string[]>
  }
  filters: {
    register(id: string, config: FilterConfig): void
    deregister(id: string): void
    registeredSelector(id: string): Selector<SearchState, boolean>
    valuesSelector(id: string): Selector<SearchState, any[]>
    setValues(id: string, values: any[]): void
    updateValues(id: string, updater: (values: any[]) => any[]): void
    initialiseValues(id: string, values: any[]): void
    selectIds: Selector<SearchState, string[]>
    activeSelector: Selector<
      SearchState,
      Record<string, FilterConfig & { values: string[] }>
    >
    registerPresentation(id: string, presentation: FilterPresentation): void
    deregisterPresentation(id: string): void
    presentation(id: string): FilterPresentation | undefined
  }
  pagination: {
    setPage(page: number): void
    setSize(size: number): void
    select: Selector<
      SearchState,
      {
        pageNumber: number
        pageStart: number
        pageEnd: number
        pageSize: number
        pageCount: number
        resultCount: number
      }
    >
  }
  search(): Promise<SearchResponse>
  clearAllFilters(): void
  getCurrentQuery(): SearchQuery
  selectHits: Selector<SearchState, any[]>
  selectStatus: Selector<
    SearchState,
    { wasSearched: boolean; loading: boolean }
  >
  store: SearchStore
  setOptions: (options: SetOptionsAction) => void
}

export type FilterPresentation = {
  displayLabel?: string
  displayValue?: (value: string) => string
}

type SetOptionsAction = object | ((prev: object) => object)

export default function useSearch({
  url,
  source: initialSource = [],
  initialFacets = {},
  initialFilters = {},
  queryThrottle = DEFAULT_SEARCH_THROTTLE_MS,
}: UseSearchProps): UseSearchReturn {
  const [store] = React.useState(() =>
    configureSearchStore({
      searchClient: createSearchClient({ queryThrottle, url }),
      initialFacets,
      initialFilters,
    }),
  )
  const [source] = React.useState(initialSource)
  const filterPresentations = React.useRef(
    new Map<string, FilterPresentation>(),
  )
  const searchOptions = React.useRef({})
  const setOptions = React.useCallback((options: SetOptionsAction) => {
    if (typeof options === "function") {
      searchOptions.current = options(searchOptions.current)
    } else {
      searchOptions.current = options
    }
  }, [])

  return React.useMemo(() => {
    const { search } = searchActions
    const {
      facetRegistered,
      facetDeregistered,
      filterRegistered,
      filterDeregistered,
      filterValuesSet,
      updateFilterValues,
      filterValuesInitialised,
      setPage,
      querySizeChanged,
      allFiltersCleared,
    } = bindActionCreators(searchActions, store.dispatch)
    type SelectorParams<InputSelector> =
      InputSelector extends Selector<SearchState, any, infer Params>
        ? Params
        : never
    type SelectorReturn<InputSelector> =
      InputSelector extends Selector<SearchState, infer Return> ? Return : never
    function createBoundSelector<
      InputSelector extends Selector<SearchState, any, any[]>,
    >(
      selector: InputSelector,
      ...params: SelectorParams<InputSelector>
    ): Selector<SearchState, SelectorReturn<InputSelector>, []> {
      return createSelector([(state) => state], (state) =>
        selector(state, ...params),
      )
    }

    return {
      facets: {
        register: (id, config) => facetRegistered({ id, config }),
        deregister: facetDeregistered,
        registeredSelector: (id) =>
          createBoundSelector(selectFacetRegistered, id),
        bucketsSelector: (id) => createBoundSelector(selectFacetBuckets, id),
        selectIds: selectFacetIds,
      },
      filters: {
        register: (id, config) => filterRegistered({ id, config }),
        deregister: filterDeregistered,
        registeredSelector: (id) =>
          createBoundSelector(selectFilterRegistered, id),
        valuesSelector: (id) => createBoundSelector(selectFilterValues, id),
        setValues: (id, values) => filterValuesSet({ id, values }),
        initialiseValues: (id, values) =>
          filterValuesInitialised({ id, values }),
        updateValues: updateFilterValues,
        selectIds: selectFilterIds,
        activeSelector: createBoundSelector(selectQueryFilters),
        registerPresentation: (id, presentation) => {
          filterPresentations.current.set(id, presentation)
        },
        deregisterPresentation: (id) => {
          filterPresentations.current.delete(id)
        },
        presentation: (id) => filterPresentations.current.get(id),
      },
      pagination: {
        setPage,
        setSize: (size) => querySizeChanged(size),
        select: selectResultsPagination,
      },
      search: () =>
        store.dispatch(search({ source, options: searchOptions.current })),
      getCurrentQuery: () =>
        selectSearchQuery(store.getState(), {
          source,
          options: searchOptions.current,
        }),
      clearAllFilters: allFiltersCleared,
      selectHits,
      selectStatus,
      store,
      setOptions,
    }
  }, [store, source, setOptions])
}
